/**
 * Content for the Landing v2 homepage.
 *
 * Split out of HomeLanding.jsx to keep both files under the 500-line limit in
 * CLAUDE.md. This module is DATA ONLY — no JSX, no styling decisions. Copy
 * carried from live HomePage.js is marked as such, and the reasoning for every
 * omission and replacement lives beside the value it applies to.
 *
 * DATA ONLY DOES NOT MEAN LITERALS ONLY. Prices are interpolated from
 * config/pricing.js rather than typed into the sentence, because a price in
 * marketing prose is the copy most likely to survive a repricing unnoticed —
 * it reads fine, it is just no longer true. Same failure as the StatTile 30px
 * and the stale "One public page" on the audit form: correct when written,
 * unreachable by the thing meant to keep it correct.
 */
import { PLAN_PRICES, ADDON_PRICES } from '@/config/pricing';

/* ── The report card's data ──────────────────────────────────────────────────
 *
 * THE SUBJECT LABEL AND THE MEASURED NUMBERS COME FROM DIFFERENT PLACES, and
 * that is a known, owner-instructed gap rather than an accident. Read this
 * before changing anything here.
 *
 * The NUMBERS are a real audit: backend/tests/fixtures/roarsinc_v2_report.json,
 * completed 2026-08-01. Every value below is transcribed from it and
 * test_homepage_report_card_matches_fixture.py pins the two together.
 *
 * The LABEL is `produit.co`, set on 2026-08-20 on the owner's instruction to
 * take the previous third-party name off the marketing surface. That name was
 * a company we do not have published permission to display, so removing it is
 * correct on its own terms.
 *
 * WHAT IS NOT YET TRUE: produit.co has not been audited. The card therefore
 * shows one product's name over another product's score. It is the shape of
 * claim this project has removed repeatedly — a named subject with a measured
 * outcome that was not measured on it — and it is only tolerable here because
 * the card reads as a product mockup rather than a case study.
 *
 * TO CLOSE IT: run an audit against produit.co, replace the fixture with that
 * report, and re-transcribe these values. The pin above will fail the build
 * until the two agree, which is exactly what it is for. Logged in
 * docs/DECISIONS.md.
 *
 * The artboard's card is invented end to end — 72/Good, "3 Critical 5 Major
 * 4 Minor", an Onboarding row, a +13/+17/-4 delta on every dimension, "3 of 12
 * annotations" and a "+9" chip. None of it is a measurement of anything.
 *
 * These numbers are transcribed from the fixture and nothing is rounded,
 * softened or improved. 58 is a worse number than 72 and it is the one we can
 * defend: a real product scoring 58 is more persuasive than an invented 72.
 *
 * Kept as a literal rather than an import because the fixture lives in the
 * backend test tree and the marketing page must not depend on it at build time.
 * test_homepage_report_card_matches_fixture.py pins the two together, so a
 * change to the audit fails the build rather than silently dating the page. */
export const DEMO_AUDIT = {
  // auditId REMOVED 2026-08-20. Nothing rendered it — it was provenance — but
  // it shipped the source audit's identifier inside the JS bundle, which is a
  // reference on the marketing surface even though no reader ever sees it.
  // The provenance is the fixture path in the header comment above, which does
  // not ship.
  appName: 'Produit',
  url: 'produit.co',
  completedAt: '2026-08-01',
  overall: 58,
  scores: {
    navigation: 70,
    visual_hierarchy: 72,
    friction_points: 65,
    accessibility: 45,
    conversion_design: 55,
    mobile_ux: 52,
  },
  // 4 findings: 0 critical, 3 major, 1 minor. Counted from the fixture, not
  // estimated. There is no critical row because there is no critical finding.
  severityCounts: { critical: 0, major: 3, minor: 1 },
  // The free tier shows the most severe finding in full plus the TITLES of the
  // rest, so the locked count is 4 - 1 = 3. The artboard says "8 more findings
  // locked · Including 2 more Critical"; both halves are invented, and the
  // second cannot be salvaged because this audit has no critical findings.
  lockedCount: 3,
  featured: {
    severity: 'major',
    dimension: 'Accessibility',
    title: 'Three blog date stamps fail contrast at 3.95:1 (required 4.5:1 for 14px normal text)',
  },
  locked: [
    { severity: 'major', dimension: 'Mobile UX', title: 'Eleven mobile tap targets are below 44x44px minimum, including logo, hamburger, and six footer nav links' },
    { severity: 'major', dimension: 'Conversion Design', title: 'No primary CTA button is present in the above-the-fold hero on desktop' },
    { severity: 'minor', dimension: 'Navigation', title: "All heading texts are duplicated verbatim (e.g., 'Industries Industries')" },
  ],
};

/* ── Feature grid ────────────────────────────────────────────────────────────
 *
 * The six cards and their copy are carried VERBATIM from live HomePage.js. The
 * dimension list in "The Score" already reads Navigation / Visual Hierarchy /
 * Friction / Accessibility / Conversion Design / Mobile UX — the artboard's
 * version still says Onboarding.
 *
 * The PREVIEW GRAPHICS are new, and three of the artboard's six are fabricated:
 *   - The Score       drew "+9 pts" over a 7-bar sparkline. Score history:
 *                     nothing tracks a previous audit. Replaced with a static
 *                     read-out of this audit's six real dimensions.
 *   - Business Impact drew "+14% est. conversion lift" over ranked NNGroup /
 *                     Baymard / Mixpanel bars. The percentage is invented and
 *                     the bars rank three research bodies against each other on
 *                     no axis at all. Replaced with the citation list, unranked.
 *   - Sprint Roadmap  drew "5 fixes / 7 fixes / 6 fixes" against filled meters.
 *                     Those totals describe an audit that does not exist; the
 *                     roadmap is real but its contents vary per audit, so the
 *                     preview shows the three sprint rows without counts. */
export const FEATURES = [
  {
    title: 'The Score',
    desc: 'See how your app performs across Navigation, Visual Hierarchy, Friction, Accessibility, Conversion Design, and Mobile UX.',
    preview: 'score',
  },
  {
    title: 'Quick Wins',
    desc: 'Identify low-effort, high-impact fixes you can ship this week.',
    preview: 'wins',
  },
  {
    title: 'Sprint Roadmap',
    desc: 'Get a prioritized backlog organized into Sprint 1, 2, and 3 based on effort and impact.',
    preview: 'sprints',
  },
  {
    title: 'Business Impact',
    desc: 'Every finding includes research-backed impact estimates with citations from NNGroup, Baymard, and Mixpanel.',
    preview: 'impact',
  },
  {
    title: 'PDF Report',
    desc: 'Download a professional PDF report to share with stakeholders or clients.',
    preview: 'pdf',
  },
  {
    title: 'White-label',
    desc: `Add your agency branding to reports for client delivery with the $${ADDON_PRICES.white_label.usd} white-label add-on.`,
    preview: 'whitelabel',
  },
];

/* ── How it works ────────────────────────────────────────────────────────────
 * Copy carried from live HomePage.js. Step 2's artboard preview drew a dashed
 * "Your previous audit" baseline across the chart — score history again, and
 * removed for the same reason. The bars are this audit's six real scores and
 * the "Weakest area" callout lands on Accessibility 45, which is true. */
export const STEPS = [
  {
    n: '01',
    label: 'Submit your app',
    title: 'Submit your app',
    desc: 'Paste your URL, or upload screenshots and PDF design exports. Our AI UX analysis engine starts scanning your product immediately, no setup, no credit card required.',
  },
  {
    n: '02',
    label: 'AI analyses 6 dimensions',
    title: 'AI analyses 6 dimensions',
    desc: 'We capture desktop and mobile screenshots, measure contrast ratios and tap-target sizes, then AI evaluates Navigation, Visual Hierarchy, Friction Points, Accessibility, Conversion Design, and Mobile UX, and every finding is checked against that evidence before it ships.',
  },
  {
    n: '03',
    label: 'Get your prioritised roadmap',
    title: 'Get your prioritised roadmap',
    desc: 'Receive a full UX audit report with a severity-ranked findings list, quick wins for this week, and a sprint-by-sprint fix roadmap ready to share with your team.',
  },
];

/* ── The six dimensions ──────────────────────────────────────────────────────
 * Titles and bodies carried from live HomePage.js, which already carries the
 * six real ones. The artboard's third cell is "Onboarding & First-Use
 * Experience" with a Mixpanel 3x retention claim; both are gone from live and
 * do not come back here.
 *
 * ORDER IS LIVE'S, NOT DimensionBars'. Live runs Nav / Visual / Mobile UX /
 * Friction / A11y / Conversion; the canonical scoring order puts Mobile UX
 * sixth. Reordering sections is a content change, so the page keeps live's
 * order and the component keeps its own. */
export const DIMENSIONS = [
  { title: 'Navigation & Information Architecture', desc: 'Can users find what they need in 3 clicks or fewer? Our AI UX analysis checks menu structure, labelling clarity, search functionality, and whether your information architecture matches how users think, not how your team built it.' },
  { title: 'Visual Hierarchy & Design Clarity', desc: 'Does the eye land on the right thing first? We evaluate CTAs, font hierarchy, whitespace usage, and colour contrast against WCAG standards. Poor visual hierarchy is the single most common cause of low conversion rates on SaaS landing pages.' },
  { title: 'Mobile UX', desc: 'We capture your page a second time at a 390px viewport and judge it there. Tap targets under 44×44px, controls spaced closely enough to mis-tap, and elements that behave differently between the desktop and mobile renders. Scored only when a mobile capture exists, never inferred from the desktop view.' },
  { title: 'Friction & Form Design', desc: "Every unnecessary field, loading state without feedback, or unclear error message costs you users. Our automated UX audit flags the friction Baymard's checkout research ties to abandonment: 22% of users have abandoned a purchase because the checkout was too long or complicated." },
  { title: 'Accessibility', desc: 'Beyond compliance, accessibility is a conversion driver. Touch targets under 44×44px lose mobile users. Missing alt text hurts SEO. Keyboard-inaccessible flows block people outright. We check all of it.' },
  { title: 'Conversion Design', desc: "Are your CTAs earning their clicks? We analyse CTA copy specificity, social proof placement, trust signal positioning, and pricing page clarity using Google's research on landing page effectiveness and conversion design principles." },
];

export const AUDIENCES = [
  {
    tab: 'SaaS Product Teams',
    title: 'SaaS Product Teams',
    body: 'Run a UX audit before every major release. Catch usability regressions early and give engineers precise fix specifications rather than vague design feedback.',
    link: '/use-cases/saas/',
    linkText: 'SaaS use cases',
  },
  {
    tab: 'UX & Design Agencies',
    title: 'UX & Design Agencies',
    body: 'Deliver professional UX audit reports to clients in hours, not days. White-label the PDF with your agency branding and use the structured findings as a baseline for your redesign pitch.',
    link: '/use-cases/agencies/',
    linkText: 'Agency use cases',
  },
  {
    tab: 'Early-Stage Startups',
    title: 'Early-Stage Startups',
    body: "Before your next funding round or user interview session, get an objective baseline score for your product's UX quality. Investors and advisors respect data, show them you're measuring what matters.",
    link: '/use-cases/startups/',
    linkText: 'Startup use cases',
  },
];

/* ── FAQ ─────────────────────────────────────────────────────────────────────
 * All five carried VERBATIM from live HomePage.js — the artboard's versions
 * still carry "onboarding" in the dimension list and a "$3,000-$15,000" price
 * range that live already removed. Live is the cleaned copy and wins.
 *
 * Q4 states "Our average audit surfaces 18 findings per product." That is an
 * unsourced number, it is already live, and content preservation means it stays
 * where it is rather than being quietly cut here. It is logged as an open item
 * and it is NOT propagated into any new copy on this page. */
export const FAQS = [
  {
    q: 'What is a UX audit and what does it include?',
    a: 'A UX audit is a systematic evaluation of a digital product’s user experience against established heuristics and best practices. A professional UX audit includes a score across key dimensions (navigation, visual hierarchy, friction, accessibility, conversion design, and mobile UX), a prioritised list of findings with severity ratings, research-backed fix recommendations, and a sprint roadmap. UXAuditPro delivers all of this automatically in under 5 minutes.',
  },
  {
    q: 'How is an AI UX audit different from a manual expert review?',
    a: 'Manual UX audits conducted by a UX consultant take days rather than minutes, and cost substantially more than an automated one. Our AI UX analysis covers the same heuristic framework in minutes, applying the same scoring rules to every audit. For most teams, AI UX audit software is the right starting point, run it before every major release to catch regressions, and commission a manual expert review before major redesigns or fundraising rounds.',
  },
  {
    q: 'What types of apps and websites can UXAuditPro audit?',
    a: 'UXAuditPro works for SaaS web applications, marketing websites, e-commerce stores, mobile app flows (via screenshots), and landing pages. Our automated UX audit tool is optimised for product interfaces, it performs best on logged-in product screens, sign-up and first-run pages, and conversion pages.',
  },
  {
    q: 'How many issues does a typical UX audit find?',
    a: 'Our average audit surfaces 18 findings per product. Of those, typically 3–5 are quick wins you can ship within a week. Teams that fix the top 3 critical issues within 30 days see measurable improvements in task completion rates and self-reported satisfaction scores.',
  },
  {
    q: 'Is the first UX audit really free?',
    a: 'Yes, your first UX audit is free, no credit card required. You get your overall score, every dimension score, and your single most severe finding in full: the evidence we captured, why it matters, and exactly how to fix it. See the complete report (every verified finding, PDF export, and annotated screenshots) for $'
      + PLAN_PRICES.STARTER.usd + ' per audit. White-label branding is an optional $'
      + ADDON_PRICES.white_label.usd + ' add-on.',
  },
];

// The same answer in "one free audit every month" wording (owner, 2026-10-07),
// shown on /home-preview/ until SEO_P1_LIVE flips, then everywhere.
export const FREE_FAQ_ONE_A_MONTH = {
  q: 'Is the free UX audit really free?',
  a: 'Yes, you get one free UX audit every month, no credit card required. You get your overall score, every dimension score, and your single most severe finding in full: the evidence we captured, why it matters, and exactly how to fix it. See the complete report (every verified finding, PDF export, and annotated screenshots) for $'
    + PLAN_PRICES.STARTER.usd + ' per audit. White-label branding is an optional $'
    + ADDON_PRICES.white_label.usd + ' add-on.',
};

/* ══ OWNER-RULED CLAIMS ═══════════════════════════════════════════════════════
 *
 * RULED BY THE OWNER, 2026-08-19, after being asked three times and shown the
 * specific problem with each figure. The instruction was to use the artboard
 * numbers as drawn and record the ruling rather than make the call silently.
 *
 * THIS IS RECORDED, NOT HIDDEN. Every number below came from the Landing v2
 * generator, not from a measurement of this product:
 *
 *   500+ apps audited      no count has been run against the audits collection
 *   92% client satisfaction  THERE IS NO CSAT OR NPS INSTRUMENT. This one cannot
 *                          be fixed by querying — the mechanism to collect it
 *                          does not exist, so there is no number to find
 *   18 avg findings        unsourced; it is already live in FAQ 4
 *   $3,000-$15,000         no rate card behind it
 *   500+ product teams     no count of distinct teams
 *
 * `<5min` is the exception and is NOT in that list: "under 5 minutes" is
 * already live site copy, in the hero and in FAQ 1, so it is preserved content
 * rather than a new claim.
 *
 * The guard that bans these strings — test_no_fabricated_social_proof_returns —
 * now carries a NAMED exception listing exactly these values and pointing here.
 * It is not a blanket waiver: any fabricated claim that is not on that list
 * still fails the build.
 *
 * TO MAKE THESE TRUE, in increasing order of difficulty: run a count for the
 * audit total; compute the mean findings across real audits; find a citable
 * source for the manual-audit range; and build a CSAT or NPS mechanism, which
 * is a product change and not a copy change.
 * ═════════════════════════════════════════════════════════════════════════ */

/** The hero pill. Three avatar swatches are decorative, not photographs. */
export const SOCIAL_PROOF = {
  // NO FIGURE HERE. 500+ was removed 2026-08-17 as unsourced (production had
  // 17 users), reinstated as 675+ on 2026-10-02, and nothing ever counted
  // anything. There is no instrument that counts distinct TEAMS at all -- not
  // a missing query, a missing concept -- so this stays null until one exists.
  // The apps-audited tile below IS now countable and is wired to the real
  // number; this one is not, and pretending otherwise is what got us here.
  label: null,
  swatches: ['#F3C6A7', '#C7D6BC', '#B6C4DC'],
};

/** The dark trust panel, between the report card and the feature grid. */
export const TRUST_STATS = {
  heading: 'Trusted by product teams shipping every week',
  tiles: [
    // OWNER-DIRECTED 2026-10-05: 750+. Unsourced, the same way 675+ was:
    // nothing on the frontend fetched GET /api/stats/public, so the null that
    // was meant to mean "ask the server" rendered as an empty tile. Logged in
    // docs/analysis/open-items.md (item Y) with what would make it sourced.
    { value: '750+', label: 'Apps Audited' },
    { value: '92%', label: 'Client Satisfaction' },
    { value: '18', label: 'Avg Findings Per Audit' },
    { value: '<5min', label: 'Time to Results' },
  ],
};

/** The Anton headline pair, immediately before pricing. */
export const HEADLINE_STATS = [
  {
    value: '<5min',
    body: 'Faster than a manual expert review that costs $3,000–$15,000',
    bars: [
      { label: 'UXAuditPro', pct: 6, strong: true },
      { label: 'Manual audit', pct: 100, strong: false },
    ],
  },
  {
    value: '92%',
    // Was a {audits} placeholder that nothing ever filled, so the braces
    // rendered. Owner-directed figure, as the trust panel tile above.
    body: 'Client satisfaction across 750+ audited products',
    note: 'SaaS teams, agencies and startups',
  },
];

/* SEO brief, Task 5: the "Guides" block above the footer. Rendered only when
   `fresh` (preview route or SEO_P1_LIVE). Anchors are plain descriptions, not
   the post titles: the checklist title still says 60-Point (open item AO). */
export const GUIDES = [
  { label: 'What is a UX audit?', href: '/ux-audit/' },
  { label: 'Free UX audit template', href: '/blog/ux-audit-template/' },
  { label: 'UX audit checklist', href: '/blog/ux-audit-checklist/' },
  { label: 'Best UX audit tools in 2026', href: '/blog/best-ux-audit-tools/' },
  { label: 'See a real audit report', href: '/sample-report/' },
  { label: 'How to audit the words in your product', href: '/blog/ux-writing-audit/' },
];

/* "Works inside Claude", directly after "Two ways to build better products"
   (owner, 2026-10-08). Rendered only when `fresh`. Every claim is one /claude/
   already makes: claudeContent.js CAPABILITIES and set-up steps, and the
   ChatGPT "Coming soon." line in ClaudePage.jsx. ChatGPT is never named here
   without "coming soon" (home-claude-section.test.js). */
export const CLAUDE_SECTION = {
  eyebrow: 'Works inside Claude',
  h2: 'Run and read your audits inside Claude',
  body: 'Connect UXAuditPro to Claude Code or claude.ai. Ask Claude to list your audits, read the findings, compare two runs of the same page, or start your free monthly audit. Paid audits always need your OK.',
  labels: ['Claude: available now', 'ChatGPT: coming soon'],
  cta: 'Set up the Claude connector',
  href: '/claude/',
};

/* The connector card beside CLAUDE_SECTION: what Claude shows when it calls
   each tool. Example data (acme.com), as the report card above uses Produit.
   Each tab's tool, mode and fields are the real ones in
   backend/mcp_app/tools.py: list_audits / get_audit / compare_audits carry
   readOnlyHint (290); compare reports resolved, new and same_page (185-195),
   never "fixed" and never a severity on new issues; start_audit is free and
   "about 4 minutes" (its docstring), capturing desktop and mobile
   (audit/claude_analyzer.py cross-viewport diff); a paid audit is
   start_paid_audit and needs confirm_spend (509). Owner-approved 2026-10-08.
   The handoff's "+13 pts" chip is not here: test_no_fabricated_claims bars a
   stated delta (docs/DECISIONS.md 2026-08-19, the "+9 pts" precedent). */
export const CLAUDE_DEMO = {
  initial: 'compare',
  tabs: [
    { key: 'list', label: 'List audits', ask: 'Which pages have I audited?', tool: 'list_audits', mode: 'read only' },
    { key: 'read', label: 'Read findings', ask: 'What are the critical findings on /checkout?', tool: 'get_audit', mode: 'read only' },
    { key: 'compare', label: 'Compare runs', ask: 'Compare my last two audits of /checkout.', tool: 'compare_audits', mode: 'read only' },
    { key: 'free', label: 'Free audit', ask: 'Run my free monthly audit on acme.com/signup.', tool: 'start_audit', mode: 'free · no approval needed' },
    { key: 'paid', label: 'Paid audit', ask: 'Run a full audit on acme.com.', tool: 'start_paid_audit', mode: 'paid · waiting for your OK' },
  ],
  list: [
    { page: 'acme.com/checkout', when: '2 days ago', score: 71 },
    { page: 'acme.com/pricing', when: '1 week ago', score: 64 },
    { page: 'acme.com/cart', when: '3 weeks ago', score: 58 },
  ],
  findings: [
    'Card form clears all fields when one input fails validation.',
    'Primary CTA falls below the fold on mobile at 390px.',
    'Shipping cost appears only on the final step.',
  ],
  compare: {
    from: 58,
    to: 71,
    stats: [
      { value: '4', label: 'resolved' },
      { value: '1', label: 'new' },
      { value: 'Same page', label: 'runs compared' },
    ],
  },
  free: {
    title: 'Free monthly audit started',
    meta: 'acme.com/signup · desktop and mobile · ready in about 4 min',
    progress: 34,
  },
  paid: {
    title: 'Full audit · acme.com',
    price: `$${PLAN_PRICES.STARTER.usd}`,
    body: "This is a paid audit. Claude won't start it until you approve.",
    approve: `Approve $${PLAN_PRICES.STARTER.usd}`,
    decline: 'Not now',
  },
};
